// First-touch acquisition attribution.
// Records the FIRST meaningful landing (utm / share-or-join trip / external referrer) once, then sent
// to the backend by flushAcquisition (acquisitionFlush.ts, called from App.tsx) once the user authenticates.
// Never overwritten — first touch wins. Best-effort: all failures are swallowed.
//
// CROSS-ORIGIN: capture happens on whichever origin the signal lands on (www./video./business.stippl.io),
// but signup/auth runs on app.stippl.io. localStorage is per-origin, so first-touch is ALSO mirrored to a
// cookie scoped to the registrable root (Domain=.stippl.io) that every subdomain can read — otherwise the
// app-origin read is always empty and nothing gets attributed.

const KEY = "stippl_acquisition_first_touch";

// domain=.stippl.io on any stippl.io host so all subdomains share the cookie; host-only on localhost/preview.
const cookieDomainAttr = (): string => {
	try {
		return /(^|\.)stippl\.io$/i.test(window.location.hostname) ? "; domain=.stippl.io" : "";
	} catch {
		return "";
	}
};

const writeFirstTouchCookie = (value: string): void => {
	try {
		if (typeof document === "undefined") return;
		const secure = window.location.protocol === "https:" ? "; secure" : "";
		// 90-day first-touch window; SameSite=Lax rides top-level navigations between subdomains.
		document.cookie = `${KEY}=${encodeURIComponent(value)}; path=/; max-age=${90 * 24 * 60 * 60}; samesite=lax${cookieDomainAttr()}${secure}`;
	} catch {
		// non-fatal — attribution is best-effort
	}
};

// Reads the first-touch cookie (raw JSON), or null. Exported so the flush reads the same shared source.
export const readFirstTouchCookie = (): string | null => {
	try {
		if (typeof document === "undefined") return null;
		const m = document.cookie.match(/(?:^|;\s*)stippl_acquisition_first_touch=([^;]*)/);
		return m ? decodeURIComponent(m[1]) : null;
	} catch {
		return null;
	}
};

type Acquisition = {
	source?: string;
	medium?: string;
	campaign?: string;
	referrer?: string;
	sourceTripId?: string; // real trip _id — only known for join links
	sourceTripSlug?: string; // decoded trip name from share links (backend resolves to an _id)
	sourceTripOwner?: string; // username segment of share links
	landingPath?: string;
	capturedAt?: string;
};

// Share view link: /{username}/trip/{tripname}   ·   Join link: /join/{tripId}
// Join links carry a real trip _id; share links carry the encodeURIComponent'd trip NAME
// (see buildTripPath in urlUtils.ts), so those are captured as slug + owner for the backend
// to resolve to an _id at profile creation.
const sourceTripFromPath = (path: string): Pick<Acquisition, "sourceTripId" | "sourceTripSlug" | "sourceTripOwner"> => {
	const join = path.match(/^\/join\/([^/?#]+)/i);
	if (join) return { sourceTripId: join[1] };
	const share = path.match(/^\/([^/]+)\/trip\/([^/?#]+)/i);
	if (share) return { sourceTripOwner: share[1], sourceTripSlug: decodeURIComponent(share[2]) };
	return {};
};

// Same-site check on the registrable root (stippl.io ↔ www./video.stippl.io are all internal).
const registrableRoot = (host: string): string => host.split(".").slice(-2).join(".");

const isExternalReferrer = (ref: string): boolean => {
	try {
		return registrableRoot(new URL(ref).hostname) !== registrableRoot(window.location.hostname);
	} catch {
		return false; // unparseable referrer = no signal
	}
};

export const captureFirstTouch = (): void => {
	try {
		if (typeof window === "undefined" || typeof localStorage === "undefined") return;
		if (localStorage.getItem(KEY) || readFirstTouchCookie()) return; // first-touch only — never overwrite (either store)

		const params = new URLSearchParams(window.location.search);
		const path = window.location.pathname || "";
		const utmSource = params.get("utm_source") || undefined;
		const sourceTrip = sourceTripFromPath(path);
		const ref = document.referrer || "";
		const externalRef = ref && isExternalReferrer(ref) ? ref : undefined;

		// Only store when there's a real acquisition signal — otherwise leave null (= direct/organic).
		if (!utmSource && !sourceTrip.sourceTripId && !sourceTrip.sourceTripSlug && !externalRef) return;

		const acq: Acquisition = {
			source: utmSource,
			medium: params.get("utm_medium") || undefined,
			campaign: params.get("utm_campaign") || undefined,
			referrer: externalRef,
			...sourceTrip,
			landingPath: path,
			capturedAt: new Date().toISOString(),
		};
		const json = JSON.stringify(acq);
		localStorage.setItem(KEY, json);
		writeFirstTouchCookie(json); // mirror to the shared .stippl.io cookie so app.stippl.io can read it
	} catch {
		// non-fatal — attribution is best-effort
	}
};

// The plugin consent page (/connect) knows which assistant sent the visitor. It calls this while still
// signed out, so the touch App.tsx's flushAcquisition sends once the new account is signed in carries
// `source`. Only fills a first touch that is absent or was captured on /connect itself, where the
// referrer was all it could see; an earlier touch from elsewhere stays (first touch wins).
export const stampFirstTouchSource = (source: string): void => {
	try {
		if (typeof window === "undefined" || typeof localStorage === "undefined") return;
		const raw = readFirstTouchCookie() || localStorage.getItem(KEY);
		const existing: Acquisition | null = raw ? JSON.parse(raw) : null;
		if (existing && existing.landingPath !== "/connect") return;
		const json = JSON.stringify({ ...existing, source, landingPath: "/connect", capturedAt: existing?.capturedAt ?? new Date().toISOString() });
		localStorage.setItem(KEY, json);
		writeFirstTouchCookie(json);
	} catch {
		// non-fatal — attribution is best-effort
	}
};
